import { Controller } from '@hotwired/stimulus'
import { EventData, HTMLFileInputElementExt } from './types'
import { humanFileSize, areFilesEqual } from '../../../utils/files'

export default class extends Controller {
	static targets = ['input', 'wrapper', 'previewContainer', 'fileContainer', 'messageContainer', 'button']

	declare readonly inputTarget: HTMLFileInputElementExt
	declare readonly hasInputTarget: boolean
	declare readonly wrapperTarget: HTMLElement
	declare readonly hasWrapperTarget: boolean
	declare readonly previewContainerTarget: HTMLElement
	declare readonly hasPreviewContainerTarget: boolean
	declare readonly fileContainerTarget: HTMLElement
	declare readonly hasFileContainerTarget: boolean
	declare readonly messageContainerTarget: HTMLElement
	declare readonly hasMessageContainerTarget: boolean
	declare readonly buttonTarget: HTMLElement
	declare readonly hasButtonTarget: boolean

	connect() {
		if (this.hasWrapperTarget) {
			this.wrapperTarget.addEventListener('dragover', this.onDragOver)
			this.wrapperTarget.addEventListener('drop', this.onDrop as EventListener)
			this.wrapperTarget.addEventListener('dragenter', this.onDragEnter as EventListener)
			this.wrapperTarget.addEventListener('dragleave', this.onDragLeave as EventListener)
		}

		this.element.addEventListener('file-input:add-error', this.onAddError as EventListener)
		this.element.addEventListener('file-input:clear-error', this.onClearError as EventListener)
		this.element.addEventListener('file-input:reset', this.onReset as EventListener)
	}

	disconnect() {
		if (this.hasWrapperTarget) {
			this.wrapperTarget.removeEventListener('dragover', this.onDragOver)
			this.wrapperTarget.removeEventListener('drop', this.onDrop as EventListener)
			this.wrapperTarget.removeEventListener('dragenter', this.onDragEnter as EventListener)
			this.wrapperTarget.removeEventListener('dragleave', this.onDragLeave as EventListener)
		}

		this.element.removeEventListener('file-input:add-error', this.onAddError as EventListener)
		this.element.removeEventListener('file-input:clear-error', this.onClearError as EventListener)
		this.element.removeEventListener('file-input:reset', this.onReset as EventListener)
	}

	clearError(): void {
		this.clearMessage()

		if (this.hasWrapperTarget) {
			this.wrapperTarget.classList.remove('error')
		}
	}

	setError(message: string): void {
		if (this.hasMessageContainerTarget) {
			this.messageContainerTarget.textContent = message
		}

		if (this.hasWrapperTarget) {
			this.wrapperTarget.classList.add('error')
		}
	}

	reset(): void {
		const input = this.resolveInput()

		if (input) {
			input.value = ''
			input.prevFiles = null
			delete input.dataset.isUploading
		}

		this.clearContainers()
		this.clearError()
	}

	clickButton(event: Event) {
		event.preventDefault()
		const input = this.resolveInput()

		if (!input || input.dataset.isUploading) {
			return
		}

		input.click()
	}

	onChange(event: Event) {
		const input = (event.target as HTMLFileInputElementExt) || this.resolveInput()
		if (!input) {
			return
		}

		this.handleMultipleFiles(input)
		input.prevFiles = input.files

		if (input.dataset.isUploading) {
			return
		}

		if (!this.hasPreviewContainerTarget || !this.hasFileContainerTarget) {
			return
		}

		this.clearContainers()

		if (!input.files || !input.files.length) {
			return
		}

		input.dataset.isUploading = '1'
		this.clearError()

		for (let index = 0; index < input.files.length; index++) {
			const file = input.files[index]

			if (this.isImageFile(file)) {
				this.renderImagePreviewItem(input, this.previewContainerTarget, file, index)
			} else {
				this.renderFileItem(input, this.fileContainerTarget, file, index)
			}
		}
	}

	onDragEnter = (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		if (this.hasWrapperTarget) {
			this.wrapperTarget.classList.add('dragover')
		}
	}

	onDragLeave = (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		if (this.hasWrapperTarget) {
			this.wrapperTarget.classList.remove('dragover')
		}
	}

	onDrop = (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		if (!this.hasWrapperTarget) {
			return
		}

		const input = this.resolveInput()

		if (!input) {
			return
		}

		if (!event.dataTransfer || !event.dataTransfer.files) {
			return
		}

		const droppedFiles = Array.from(event.dataTransfer.files)
		const dataTransfer = new DataTransfer()

		if (!input.multiple || !input.files) {
			droppedFiles.forEach((file) => dataTransfer.items.add(file))
		} else {
			Array.from(input.files).forEach((file) => dataTransfer.items.add(file))

			droppedFiles.forEach((droppedFile) => {
				const fileExists = Array.from(input.files!).some((existingFile) => 
					areFilesEqual(droppedFile, existingFile)
				)

				if (!fileExists) {
					dataTransfer.items.add(droppedFile)
				}
			})
		}

		input.files = dataTransfer.files
		input.dispatchEvent(new Event('change', { bubbles: true }))
	}

	onDragOver = (event: DragEvent) => {
		event.preventDefault()
	}

	private handleMultipleFiles(input: HTMLFileInputElementExt) {
		const dataTransfer = new DataTransfer()
		const previousFiles = input.prevFiles

		if (previousFiles && input.multiple) {
			Array.from(previousFiles).forEach((file) => dataTransfer.items.add(file))

			if (input.files) {
				Array.from(input.files).forEach((newFile) => {
					const fileExists = Array.from(previousFiles).some((existingFile) => 
						areFilesEqual(newFile, existingFile)
					)

					if (!fileExists) {
						dataTransfer.items.add(newFile)
					}
				})
			}

			if (dataTransfer.files.length) {
				input.files = dataTransfer.files
			} else {
				input.files = null
				input.value = ''
			}
		}
	}

	private clearContainers() {
		if (this.hasPreviewContainerTarget) {
			this.previewContainerTarget.innerHTML = ''
		}

		if (this.hasFileContainerTarget) {
			this.fileContainerTarget.innerHTML = ''
		}
	}

	private clearMessage() {
		if (this.hasMessageContainerTarget) {
			this.messageContainerTarget.textContent = ''
		}
	}

	private onAddError = (event: CustomEvent) => {
		const message = (event.detail && (event.detail.message as string)) || ''

		if (!message) {
			return
		}

		this.setError(message)
	}

	private onClearError = () => {
		this.clearError()
	}

	private onReset = () => {
		this.reset()
	}

	private isImageFile(file: File): boolean {
		return file.type.match(/^.*(jpeg|jpg|png|gif)$/) !== null
	}

	private deleteInputFile(file: File, input: HTMLFileInputElementExt) {
		if (!input.multiple) {
			input.value = ''
			return
		}

		const dataTransfer = new DataTransfer()

		if (input.files) {
			for (let i = 0; i < input.files.length; i++) {
				if (!areFilesEqual(file, input.files[i])) {
					dataTransfer.items.add(input.files[i])
				}
			}
		}

		if (dataTransfer.files.length) {
			input.files = dataTransfer.files
			input.prevFiles = dataTransfer.files
		} else {
			input.value = ''
			input.prevFiles = null
		}
	}

	private renderImagePreviewItem(input: HTMLFileInputElementExt, container: HTMLElement, file: File, index: number) {
		const reader = new FileReader()
		const imageContainer = document.createElement('div')
		imageContainer.classList.add('file-input__preview-item--wrapper')
		const imageItem = document.createElement('div')
		imageItem.classList.add('file-input__preview-item', 'loading')
		const image = document.createElement('div')
		image.classList.add('file-input__preview-image')
		const message = document.createElement('div')
		message.classList.add('t-caption')
		const imageDelete = document.createElement('button')
		imageDelete.classList.add('file-input__preview-image--delete')
		const imageReload = document.createElement('button')
		imageReload.classList.add('file-input__preview-image--reload')

		const loadEventData: EventData = { file, input, index, promise: null, errorMessage: null, container: imageItem }
		const deleteEventData: EventData = { file, input, index, promise: null, errorMessage: null, container: imageItem }

		const dispatchLoadEvent = () => {
			input.dispatchEvent(new CustomEvent('uploadFile', { detail: loadEventData }))
			if (!loadEventData.promise) {
				input.dataset.isUploading = ''
				reader.readAsDataURL(file)
				return
			}
			loadEventData.promise
				.then(() => {
					reader.readAsDataURL(file)
					input.dataset.isUploading = ''
				})
				.catch(() => {
					imageItem.classList.remove('loading')
					imageItem.classList.add('error')
					imageItem.append(imageReload)
					input.dataset.isUploading = ''
					message.innerHTML = loadEventData.errorMessage || 'Не удалось загрузить файл'
				})
		}

		const dispatchDeleteEvent = () => {
			input.dispatchEvent(new CustomEvent('deleteFile', { detail: deleteEventData }))
			if (!deleteEventData.promise) {
				input.dataset.isUploading = ''
				this.deleteInputFile(file, input)
				imageContainer.remove()
				return
			}
			deleteEventData.promise
				.then(() => {
					input.dataset.isUploading = ''
					this.deleteInputFile(file, input)
					imageContainer.remove()
				})
				.catch(() => {
					input.dataset.isUploading = ''
					imageItem.classList.remove('loading')
					imageItem.classList.add('error')
					message.innerHTML = deleteEventData.errorMessage || 'Не удалось удалить файл'
				})
		}

		const deleteImagePreviewItem = (event: Event) => {
			event.preventDefault()
			event.stopPropagation()

			if (input.dataset.isUploading) {
				return
			}

			imageItem.classList.add('loading')
			imageItem.classList.remove('error')
			input.dataset.isUploading = '1'
			dispatchDeleteEvent()
		}

		const reloadImagePreviewItem = (event: Event) => {
			event.preventDefault()
			event.stopPropagation()

			if (input.dataset.isUploading) {
				return
			}

			imageReload.remove()
			imageItem.classList.add('loading')
			imageItem.classList.remove('error')
			input.dataset.isUploading = '1'
			dispatchLoadEvent()
		}

		imageDelete.addEventListener('click', deleteImagePreviewItem)
		imageReload.addEventListener('click', reloadImagePreviewItem)

		reader.onload = () => {
			image.style.backgroundImage = `url(${reader.result as string})`
			imageItem.classList.remove('loading')
			imageItem.append(imageDelete)
			imageItem.append(image)
		}

		imageContainer.append(imageItem)
		imageContainer.append(message)
		container.append(imageContainer)
		dispatchLoadEvent()
	}

	private renderFileItem(input: HTMLFileInputElementExt, container: HTMLElement, file: File, index: number) {
		const fileItem = document.createElement('div')
		fileItem.classList.add('file-input__file-item', 'loading')
		const fileName = document.createElement('div')
		fileName.classList.add('t-body', 'file-input__file-item--name')
		fileName.innerHTML = file.name
		const fileSize = document.createElement('div')
		fileSize.classList.add('t-body', 'file-input__file-item--size')
		fileSize.innerHTML = humanFileSize(file.size)
		const deleteButton = document.createElement('button')
		deleteButton.classList.add('file-input__file-item--delete')
		const reloadButton = document.createElement('button')
		reloadButton.classList.add('file-input__file-item--reload')
		const message = this.hasMessageContainerTarget ? this.messageContainerTarget : null

		const loadEventData: EventData = { file, input, index, promise: null, errorMessage: null, container: fileItem }
		const deleteEventData: EventData = { file, input, index, promise: null, errorMessage: null, container: fileItem }

		const dispatchLoadEvent = () => {
			input.dispatchEvent(new CustomEvent('uploadFile', { detail: loadEventData }))
			if (!loadEventData.promise) {
				input.dataset.isUploading = ''
				fileItem.classList.remove('loading')
				fileItem.append(deleteButton)
				reloadButton.remove()
				return
			}
			loadEventData.promise
				.then(() => {
					input.dataset.isUploading = ''
					fileItem.classList.remove('loading')
					fileItem.append(deleteButton)
					reloadButton.remove()
				})
				.catch(() => {
					input.dataset.isUploading = ''
					fileItem.classList.remove('loading')
					fileItem.classList.add('error')
					fileItem.append(reloadButton)

					if (message) {
						message.innerHTML = loadEventData.errorMessage || 'Не удалось загрузить файл'
					}
				})
		}

		const dispatchDeleteEvent = () => {
			input.dispatchEvent(new CustomEvent('deleteFile', { detail: deleteEventData }))
			if (!deleteEventData.promise) {
				input.dataset.isUploading = ''
				this.deleteInputFile(file, input)

				if (message) {
					message.innerHTML = ''
				}

				fileItem.remove()
				return
			}
			deleteEventData.promise
				.then(() => {
					input.dataset.isUploading = ''
					this.deleteInputFile(file, input)

					if (message) {
						message.innerHTML = ''
					}

					fileItem.remove()
				})
				.catch(() => {
					input.dataset.isUploading = ''
					fileItem.classList.remove('loading')
					fileItem.classList.add('error')

					if (message) {
						message.innerHTML = deleteEventData.errorMessage || 'Не удалось удалить файл'
					}
				})
		}

		const deleteFileItem = (event: Event) => {
			event.preventDefault()
			event.stopPropagation()

			if (input.dataset.isUploading) {
				return
			}

			input.dataset.isUploading = '1'
			fileItem.classList.add('loading')
			fileItem.classList.remove('error')
			dispatchDeleteEvent()
		}

		const reloadFileItem = (event: Event) => {
			event.preventDefault()
			event.stopPropagation()

			if (input.dataset.isUploading) {
				return
			}

			input.dataset.isUploading = '1'

			if (message) {
				message.innerHTML = ''
			}

			fileItem.classList.add('loading')
			fileItem.classList.remove('error')
			dispatchLoadEvent()
		}

		deleteButton.addEventListener('click', deleteFileItem)
		reloadButton.addEventListener('click', reloadFileItem)

		fileItem.append(fileName)
		fileItem.append(fileSize)
		container.append(fileItem)
		dispatchLoadEvent()
	}

	private resolveInput(): HTMLFileInputElementExt | null {
		if (this.hasInputTarget) {
			return this.inputTarget
		}

		return this.element.querySelector('input[type="file"].file-input') as HTMLFileInputElementExt | null
	}
}
